import React from 'react'
import Html from 'react-pdf-html'
import ReactPDF, { Document, Page, StyleSheet } from '@react-pdf/renderer'

import { actionUpload } from './action-upload'
import { APP_PDF_UPLOAD_PATH, PAPER_SIZE_MAP } from './constants'
import { createImageComponent, createSpacingComponent, createTextComponent, extractImageUrls } from './pdf-elements'

const styles = StyleSheet.create({
  // Page
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
  },

  // Sections
  section: {
    marginBottom: 10,
  },
})

/**
 * Simple filename sanitization function
 */
function sanitizeFileName(filename: string): string {
  return filename.replace(/[^a-zA-Z0-9._-]/g, '_')
}

/**
 * Registers basic fonts for PDF generation
 */
function registerPDFFonts() {
  try {
    // Register common system fonts with fallback fonts
    ReactPDF.Font.register({
      family: 'system-ui',
      fonts: [
        { src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2' },
      ],
    })

    ReactPDF.Font.register({
      family: 'Arial',
      fonts: [
        { src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2' },
      ],
    })

    ReactPDF.Font.register({
      family: 'Inter',
      fonts: [
        {
          src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
          fontWeight: 400,
          fontStyle: 'normal',
        },
        {
          src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
        },
      ],
    })

    ReactPDF.Font.register({
      family: 'sans-serif',
      fonts: [
        {
          src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
          fontWeight: 400,
          fontStyle: 'normal',
        },
        {
          src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2',
        },
      ],
    })

    ReactPDF.Font.register({
      family: 'Helvetica',
      fonts: [
        { src: 'https://fonts.gstatic.com/s/inter/v13/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2' },
      ],
    })
  } catch (error) {
    // Silently fail font registration to not break PDF generation
    console.warn('Font registration failed, using default fonts:', error)
  }
}

/**
 * Sanitizes HTML content to ensure compatibility with react-pdf-html
 * @param htmlContent - Raw HTML content
 * @returns Sanitized HTML content
 */
function sanitizeHTMLForPDF(htmlContent: string): string {
  try {
    // Replace problematic font families with safe alternatives
    const defaultFontStack = 'Inter, Arial, sans-serif'
    let sanitized = htmlContent
      // System fonts
      .replace(/font-family:\s*system-ui[^;]*/gi, `font-family: ${defaultFontStack}`)
      .replace(/font-family:\s*-apple-system[^;]*/gi, `font-family: ${defaultFontStack}`)
      .replace(/font-family:\s*BlinkMacSystemFont[^;]*/gi, `font-family: ${defaultFontStack}`)
      .replace(/font-family:\s*"Segoe UI"[^;]*/gi, `font-family: ${defaultFontStack}`)
      .replace(/font-family:\s*ui-sans-serif[^;]*/gi, `font-family: ${defaultFontStack}`)
      // Generic font families
      .replace(/font-family:\s*sans-serif[^;]*/gi, `font-family: ${defaultFontStack}`)
      // Ensure single quotes are handled
      .replace(/font-family:\s*'[^']*'[^;]*/gi, `font-family: ${defaultFontStack}`)
      // Handle font-family declarations with multiple fonts
      .replace(/font-family:\s*([^;]*)/gi, (match) => {
        // If it already contains Inter, leave it as is
        if (match.toLowerCase().includes('inter')) {
          return match
        }
        // Otherwise, prepend Inter to the font stack
        return `font-family: Inter, ${match.replace('font-family:', '').trim()}`
      })

    // Remove or replace other problematic CSS properties
    sanitized = sanitized
      .replace(/text-rendering:\s*[^;]*;?/gi, '') // Remove text-rendering
      .replace(/-webkit-[^:]*:\s*[^;]*;?/gi, '') // Remove webkit-specific properties
      .replace(/-moz-[^:]*:\s*[^;]*;?/gi, '') // Remove moz-specific properties
      .replace(/user-select:\s*[^;]*;?/gi, '') // Remove user-select
      .replace(/appearance:\s*[^;]*;?/gi, '') // Remove appearance

    return sanitized
  } catch (error) {
    console.warn('HTML sanitization failed, using original content:', error)
    return htmlContent
  }
}

/**
 * Processes a line that contains image URLs and text
 * @param line - Text line containing images
 * @param lineIndex - Index of the line
 * @param isLastLine - Whether this is the last line
 * @returns Array of React elements (text and images)
 */
function processLineWithImages(line: string, lineIndex: number, isLastLine: boolean): React.ReactElement[] {
  const components: React.ReactElement[] = []
  const imageUrls = extractImageUrls(line)
  let remainingText = line
  let componentIndex = 0

  imageUrls.forEach((imageUrl) => {
    const urlIndex = remainingText.indexOf(imageUrl)

    // Add text before the image URL if any
    if (urlIndex > 0) {
      const textBefore = remainingText.substring(0, urlIndex).trim()
      if (textBefore) {
        components.push(createTextComponent(textBefore, `text-${lineIndex}-${componentIndex++}`))
      }
    }

    // Add the image component
    components.push(createImageComponent(imageUrl, `image-${lineIndex}-${componentIndex++}`))

    // Update remaining text
    remainingText = remainingText.substring(urlIndex + imageUrl.length)
  })

  // Add any remaining text after the last image
  if (remainingText.trim()) {
    const marginBottom = isLastLine ? 0 : 4
    components.push(createTextComponent(remainingText.trim(), `text-after-${lineIndex}`, marginBottom))
  }

  return components
}

/**
 * Processes a single text line without images
 * @param line - Text line
 * @param lineIndex - Index of the line
 * @param isLastLine - Whether this is the last line
 * @returns React Text element
 */
function processTextLine(line: string, lineIndex: number, isLastLine: boolean): React.ReactElement {
  const marginBottom = isLastLine ? 0 : 4
  return createTextComponent(line, `text-${lineIndex}`, marginBottom)
}

/**
 * Processes a single line and returns appropriate components
 * @param line - Text line to process
 * @param lineIndex - Index of the line
 * @param totalLines - Total number of lines
 * @returns Array of React elements
 */
function processLine(line: string, lineIndex: number, totalLines: number): React.ReactElement[] {
  const isLastLine = lineIndex === totalLines - 1

  // Handle empty lines
  if (line.trim() === '') {
    return [createSpacingComponent(`empty-${lineIndex}`)]
  }

  // Check if line contains images
  const imageUrls = extractImageUrls(line)

  if (imageUrls.length > 0) {
    return processLineWithImages(line, lineIndex, isLastLine)
  } else {
    return [processTextLine(line, lineIndex, isLastLine)]
  }
}

/**
 * Converts plain text to PDF-compatible text components, respecting newlines and parsing image URLs
 * @param textContent - Plain text content
 * @returns Array of text and image components
 */
function convertTextToComponents(textContent: string): React.ReactElement[] {
  // Split by newlines and preserve empty lines for spacing
  const lines = textContent.split('\n').filter((line) => line.trim() !== '' || line === '')

  // Process each line and flatten the results
  return lines.flatMap((line, index) => processLine(line, index, lines.length))
}

/**
 * Generates a PDF from HTML content and returns a blob
 * @param htmlContent - The HTML content to convert to PDF
 * @param filename - The filename for the PDF (optional, for metadata)
 * @param paperSize - The paper size to use (default: 'A4')
 * @returns Promise<Blob> - The generated PDF as a blob
 */
async function generatePDFFromHTML(
  htmlContent: string,
  filename?: string,
  paperSize: keyof typeof PAPER_SIZE_MAP = 'A4'
): Promise<Blob> {
  try {
    // Register fonts before PDF generation
    registerPDFFonts()

    // Sanitize HTML content to remove problematic CSS
    const sanitizedHTML = sanitizeHTMLForPDF(htmlContent)

    // Get paper size dimensions
    const size = PAPER_SIZE_MAP[paperSize]

    // Create a PDF document component
    const PDFDocument = () => {
      const HtmlComponent = Html as any
      return React.createElement(
        Document,
        { title: filename },
        React.createElement(
          Page,
          {
            size: [size.width, size.height],
            style: styles.page,
          },
          React.createElement(
            'View',
            { style: styles.section },
            React.createElement(
              HtmlComponent,
              {
                resetStyles: false,
              },
              sanitizedHTML
            )
          )
        )
      )
    }

    // Generate the PDF
    const pdfDoc = ReactPDF.pdf(React.createElement(PDFDocument))
    const blob = await pdfDoc.toBlob()

    return blob
  } catch (error) {
    console.error('Error generating PDF from HTML:', error)
    throw new Error(`Failed to generate PDF from HTML: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Generates a PDF from plain text content and returns a blob
 * @param textContent - The plain text content to convert to PDF
 * @param filename - The filename for the PDF (optional, for metadata)
 * @param paperSize - The paper size to use (default: 'A4')
 * @returns Promise<Blob> - The generated PDF as a blob
 */
async function generatePDFFromText(
  textContent: string,
  filename?: string,
  paperSize: keyof typeof PAPER_SIZE_MAP = 'A4'
): Promise<Blob> {
  try {
    // Register fonts before PDF generation
    registerPDFFonts()

    // Get paper size dimensions
    const size = PAPER_SIZE_MAP[paperSize]

    // Convert text to components
    const textComponents = convertTextToComponents(textContent)

    // Create a PDF document component
    const PDFDocument = () => {
      return React.createElement(
        Document,
        { title: filename },
        React.createElement(
          Page,
          {
            size: [size.width, size.height],
            style: styles.page,
          },
          React.createElement('View', { style: styles.section }, ...textComponents)
        )
      )
    }

    // Generate the PDF
    const pdfDoc = ReactPDF.pdf(React.createElement(PDFDocument))
    const blob = await pdfDoc.toBlob()

    return blob
  } catch (error) {
    console.error('Error generating PDF from text:', error)
    throw new Error(`Failed to generate PDF from text: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Generates a PDF from content (HTML or text) based on the format flag
 * @param content - The content to convert to PDF
 * @param filename - The filename for the PDF (optional, for metadata)
 * @param paperSize - The paper size to use (default: 'A4')
 * @param useHtmlFormat - Whether to treat content as HTML (default: false)
 * @returns Promise<Blob> - The generated PDF as a blob
 */
async function generatePDF(
  content: string,
  filename?: string,
  paperSize: keyof typeof PAPER_SIZE_MAP = 'A4',
  useHtmlFormat: boolean = false
): Promise<Blob> {
  if (useHtmlFormat) {
    return generatePDFFromHTML(content, filename, paperSize)
  } else {
    return generatePDFFromText(content, filename, paperSize)
  }
}

/**
 * Uploads a PDF blob to the organization's storage in a "pdf" folder
 * @param blob - The PDF blob to upload
 * @param filename - The filename for the uploaded file
 * @param orgId - The organization ID
 * @returns Promise<string> - The URL of the uploaded file
 */
async function uploadPDFBlob(blob: Blob, filename: string, orgId: string): Promise<string> {
  try {
    // Ensure filename has .pdf extension and is sanitized
    const sanitizedFilename = sanitizeFileName(filename.endsWith('.pdf') ? filename : `${filename}.pdf`)

    // let pdfFolderId = ''

    // // access storage folders api, currently only support in app builder (preview mode)
    // if (window.appMode === 'preview') {
    //   // First, get the list of folders to check if "pdf" folder exists
    //   const folders = await StorageFoldersAPI.ReqList('', orgId)
    //   let pdfFolder = folders.find((folder: IFolderBrief) => folder._Name.toLowerCase() === APP_PDF_UPLOAD_FOLDER_NAME)

    //   // If "pdf" folder doesn't exist, create it
    //   if (!pdfFolder) {
    //     pdfFolder = await StorageFoldersAPI.ReqCreate({
    //       _OrgID: orgId,
    //       _Name: APP_PDF_UPLOAD_FOLDER_NAME,
    //       // No _ParentID means it's in the root
    //     })
    //   }
    //   pdfFolderId = pdfFolder._id
    // }

    // Convert blob to File object for upload
    const file = new File([blob], sanitizedFilename, { type: 'application/pdf' })

    const uploadResult = await actionUpload(file, orgId, APP_PDF_UPLOAD_PATH)

    const pdfUrl = uploadResult._File._Link

    return pdfUrl
  } catch (error) {
    console.error('Error uploading PDF:', error)
    throw new Error(`Failed to upload PDF: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Complete PDF generation workflow: Content to PDF to upload
 * @param content - The content to convert (HTML or text)
 * @param filename - The filename for the PDF
 * @param orgId - The organization ID for storage upload
 * @param paperSize - The paper size to use (default: 'A4')
 * @param useHtmlFormat - Whether to treat content as HTML (default: false)
 * @returns Promise<string> - The URL of the uploaded PDF
 */
export default async function generateAndUploadPDF(
  content: string,
  filename: string,
  orgId: string,
  paperSize: keyof typeof PAPER_SIZE_MAP = 'A4',
  useHtmlFormat: boolean = false
): Promise<string> {
  // Ensure filename has .pdf extension
  const pdfFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`

  // Generate PDF blob
  const pdfBlob = await generatePDF(content, pdfFilename, paperSize, useHtmlFormat)

  // Upload and get URL
  const pdfUrl = await uploadPDFBlob(pdfBlob, pdfFilename, orgId)

  return pdfUrl
}
